import type { VariableFilter } from "@hightouch/htui";
import { UNKNOWN_VARIABLE_MESSAGE } from "src/cep/messages/constants";
import {
  type MergeTagColumn,
  type RenderedOutput,
  isEmailRenderedOutput,
} from "src/cep/messages/types";
import {
  getDefaultFilterValue,
  type LiquidVariableFilter,
  parseLiquidVariables,
} from "src/utils/liquid-template";

export function buildDisplayNameMap(
  mergeTagOptions: MergeTagColumn[],
): Record<string, string> {
  return Object.fromEntries(
    mergeTagOptions.map((c) => [c.template_key, c.display_name]),
  );
}

export const MERGE_TAG_REGEX = /\{\{\s*([^}]+)\s*\}\}/g;

/**
 * Pure raw-string → structured merge-tag pill parse, shared by the CodeMirror
 * pill extension and the TipTap pill node. Given a `{{ … }}` snippet and a
 * `template_key → label` map, resolves the friendly label (falling back to the
 * variable name), the applied filters, and the default-filter fallback value.
 */
export function parseMergeTagPill(
  raw: string,
  labelMap: Record<string, string> = {},
): {
  variableName: string;
  filters: LiquidVariableFilter[];
  raw: string | null;
  label: string;
  fallback: string | undefined;
} {
  const parsed = parseLiquidVariables(raw)[0];
  const variableName = parsed?.variableName ?? "";
  const filters = parsed?.filters ?? [];
  return {
    variableName,
    filters,
    raw: parsed?.raw ?? null,
    label: labelMap[variableName] ?? variableName,
    fallback: getDefaultFilterValue(filters),
  };
}

/**
 * Whether an email HTML body has no visible content — only structural/wrapper
 * markup with nothing to show (e.g. a Beefree "empty editor state" export, or content
 * the user cleared then saved). Used to decide whether to render the email empty
 * state instead of a blank preview iframe / thumbnail.
 *
 * A raw `html.length > 0` check is not enough: clearing the editor still saves a
 * non-empty wrapper. Conservative — any visible text or media (image, video, or
 * inline background-image) counts as content, so a real email is never hidden.
 */
export function isEmailHtmlEmpty(html: string | null | undefined): boolean {
  if (!html || !html.trim()) return true;

  const body = new DOMParser().parseFromString(html, "text/html").body;
  if (!body) return true;

  // Non-breaking spaces render as blank, so don't count them as text.
  if ((body.textContent ?? "").replace(/\u00a0/g, "").trim().length > 0) {
    return false;
  }
  if (body.querySelector("img, picture, video, svg, iframe")) return false;

  // Hero/banner emails can be image-only via an inline background-image.
  return !Array.from(body.querySelectorAll<HTMLElement>("[style]")).some((el) =>
    /background(-image)?\s*:[^;]*url\(/i.test(el.getAttribute("style") ?? ""),
  );
}

/**
 * Whether an email message already has content or metadata that applying a
 * template would overwrite.
 */
export function emailHasContentOrMetadata({
  renderedOutput,
  subject,
  preheader,
}: {
  renderedOutput: RenderedOutput | undefined;
  subject: string | null | undefined;
  preheader: string | null | undefined;
}): boolean {
  const hasVisibleBody =
    isEmailRenderedOutput(renderedOutput) &&
    !isEmailHtmlEmpty(renderedOutput.html);
  const hasMetadata = Boolean(subject?.trim()) || Boolean(preheader?.trim());
  return hasVisibleBody || hasMetadata;
}

// Given the inner text of a {{ ... }} tag, return a friendly display name.
// Extracts the variable name from bracket-access forms like `["First Name"]`.
export function getMergeTagDisplayName(innerText: string): string {
  const trimmed = innerText.trim();

  // Extract variable name from bracket-access: ["Name"] or ['Name']
  const bracketMatch = trimmed.match(/\[\s*["']([^"']+)["']\s*\]/);
  if (bracketMatch?.[1]) return bracketMatch[1];

  // Strip any filters for plain variable names
  const withoutFilters = trimmed.replace(/\|.*$/, "").trim();
  return withoutFilters || trimmed;
}

const segmenter = new Intl.Segmenter(undefined, { granularity: "grapheme" });

export function countGraphemes(text: string): number {
  let count = 0;
  for (const _ of segmenter.segment(text)) {
    count++;
  }
  return count;
}

export function buildLiquidVariableStyle({
  borderColor,
  textColor,
  backgroundColor = "none",
}: {
  borderColor: string;
  textColor: string;
  backgroundColor?: string;
}): string {
  return `color: ${textColor}; border: 1px dashed ${borderColor}; border-radius: 4px; padding: 4px; font-family: monospace; font-weight: 500; background-color: ${backgroundColor};`;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Inline styles emitted on the chip-pill DOM. Single-quoted attributes
// (`style='...'`) let the CSS values keep their double quotes (e.g.
// "DM Mono", "salt") without escaping or breaking attribute parsing.
//
// These cell styles mirror @hightouch/htui's Variable component (the layout
// intentionally differs — see buildLiquidVariableHtml, which expands filters
// rather than collapsing to `+N`). This is the only place they live, across the
// CEP editor canvas and email-preview iframes, which are sandboxed / a
// third-party canvas and can't reach htui's Emotion styles, so the pill must be
// self-contained inline HTML. (The in-app CodeMirror pill renders the real
// component through a portal instead — see merge-tag-pill-extension.)
//
// The pill inherits its text color from the surrounding body text (black on
// light, white on dark) rather than hardcoding a teal — the sandboxed surfaces
// can't read Chakra's CSS variables, and a fixed teal clashes against arbitrary
// email backgrounds. The dashed border and cell backgrounds below derive from
// the inherited color via `oklch(from currentColor …)` (the same DERIVED_BORDER
// / DERIVED_CELL_BG formulas htui uses).
//
// The label column has a 0 minimum width, so a table cell in an email can shrink the pill and the label truncates.
// A pill held at its full text width widens every row of a table-based email.
const PILL_STYLE = `display:inline-grid;grid-auto-flow:column;grid-template-columns:minmax(0,auto);grid-auto-columns:max-content;align-items:center;border:1px dashed oklch(from currentColor l calc(c * 1.4) h / 0.4);border-radius:4px;font-family:"DM Mono",Menlo,Monaco,Consolas,"Liberation Mono","Courier New",monospace;font-feature-settings:"salt" on,"ss01" on,"ss02" on,"ss03" on,"ss04" on,"ss05" on;font-size:calc(1em - 1px);font-weight:500;line-height:1.5;height:1.71em;min-width:1.71em;max-width:100%;overflow:hidden;white-space:nowrap;text-transform:none`;

const CELL_PADDING = "padding:0 calc(0.5em - 3.5px)";

const CELL_STYLE = `overflow:hidden;text-overflow:ellipsis;${CELL_PADDING}`;

const APPENDED_CELL_STYLE = `display:flex;align-items:center;height:100%;${CELL_PADDING};background:oklch(from currentColor l calc(c * 1.6) h / calc(0.1 + l * 0.1));border-left:1px dashed oklch(from currentColor l calc(c * 1.4) h / 0.4)`;

// htui danger.text (red.700) on danger.background (red.50), matching the Beefree canvas's unknown merge tag in beefree.css.
const UNKNOWN_PILL_STYLE =
  "color:oklch(43.46% 0.174 18.79);background:oklch(96.91% 0.015 17.42)";

const UNKNOWN_PILL_WRAPPER_STYLE =
  "position:relative;display:inline-block;max-width:100%";

/**
 * Emits canvas-display markup based on @hightouch/htui's Variable component: an
 * outer dashed-border pill whose first cell is the variable name, followed by an
 * appended cell (lighter background, dashed left divider) per applied filter.
 *
 * Unlike the component — which collapses trailing filters into a `+N` cell that
 * reveals them on hover — this renders **every** filter as its own cell. These
 * surfaces (Beefree canvas, sandboxed preview iframe) can't mount htui's tooltip,
 * so a `+N` count would hide filters the reader has no way to reveal. The
 * interactive CodeMirror pill still uses the real component with `+N` collapse.
 * Styling is inline so the chip renders with no CSS coupling.
 *
 * Display-only — the chip is never persisted or fed to the backend renderer.
 * Beefree-canvas exports run through `canonicalizeContentBlocksInHtml`
 * (in `content-block-json-transform.ts`), which strips each
 * `<!--ht-content-block:UUID-->...</--ht-content-block-->` wrapper down to
 * its canonical `{% ht_content_block id:"..." %}` tag — chips inside the
 * wrapper vanish with the body and the renderer re-resolves from the block's
 * `row_html` (which still carries raw `{{ ... }}`).
 */
export function buildLiquidVariableHtml({
  label,
  filters,
  isUnknown = false,
}: {
  label: string;
  filters: VariableFilter[];
  /** Names a variable the send can't fill. The border and filter cells derive from the red text color. */
  isUnknown?: boolean;
}): string {
  const labelCell = `<span style='${CELL_STYLE}'>${escapeHtml(label)}</span>`;
  const filterCells = filters
    .map(
      (filter) =>
        `<span style='${APPENDED_CELL_STYLE}'>${escapeHtml(filter.label)}</span>`,
    )
    .join("");
  if (!isUnknown) {
    return `<span style='${PILL_STYLE}'>${labelCell}${filterCells}</span>`;
  }
  // The pill clips its overflow, so the tooltip hangs off a wrapper instead.
  return `<span ${UNKNOWN_VARIABLE_TOOLTIP_ATTRIBUTE}='${escapeHtml(UNKNOWN_VARIABLE_MESSAGE)}' style='${UNKNOWN_PILL_WRAPPER_STYLE}'><span style='${PILL_STYLE};${UNKNOWN_PILL_STYLE}'>${labelCell}${filterCells}</span></span>`;
}

/** Carries an unknown pill's tooltip text, which the preview iframe's stylesheet draws on hover. */
export const UNKNOWN_VARIABLE_TOOLTIP_ATTRIBUTE = "data-ht-unknown-variable";

// htui's Tooltip, resolved to literal values because the sandboxed iframe can't read Chakra's CSS variables.
export const UNKNOWN_VARIABLE_TOOLTIP_STYLE =
  `[${UNKNOWN_VARIABLE_TOOLTIP_ATTRIBUTE}]:hover::after{` +
  `content:attr(${UNKNOWN_VARIABLE_TOOLTIP_ATTRIBUTE});position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);z-index:2147483647;pointer-events:none;white-space:nowrap;` +
  "padding:4px 10px;border-radius:8px;background:oklch(0 0 0 / 80%);color:white;backdrop-filter:blur(8px);" +
  "box-shadow:0 0 0 0.75px rgba(0,0,0,0.4) inset,0 0 0 1.5px rgba(255,255,255,0.24) inset,0 8px 24px 0 rgba(0,0,0,0.2),0 2px 8px 0 rgba(0,0,0,0.08);" +
  "font-family:Lausanne,system-ui,sans-serif;font-size:12px;font-weight:500;line-height:16px;text-align:center;text-transform:none;letter-spacing:normal}";
